import { Bell, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";

import { setLanguage } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function ShellHeaderActions({ initials = "CC" }: { initials?: string }) {
  const { t, i18n } = useTranslation();
  const current = i18n.language === "ar" ? "ar" : "en";

  return (
    <div className="ms-auto flex items-center gap-1 rounded-full border border-border/70 bg-background/70 p-1 shadow-soft">
      <button
        type="button"
        onClick={() => setLanguage(current === "ar" ? "en" : "ar")}
        className="inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        aria-label={t("header.language")}
      >
        <Globe className="h-4 w-4" />
        <span>{current === "ar" ? "EN" : "عربي"}</span>
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="relative grid h-9 w-9 place-items-center rounded-full text-foreground transition-colors hover:bg-muted"
            aria-label={t("adminShell.notifications")}
          >
            <Bell className="h-5 w-5" />
            <span className="absolute end-2 top-2 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-background" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>{t("headerMenu.notifications", "Notifications")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="flex-col items-start gap-0.5">
            <span className="text-sm font-medium">{t("headerMenu.notif1Title", "New booking received")}</span>
            <span className="text-xs text-muted-foreground">{t("headerMenu.notif1Time", "2 minutes ago")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem className="flex-col items-start gap-0.5">
            <span className="text-sm font-medium">{t("headerMenu.notif2Title", "Payout processed")}</span>
            <span className="text-xs text-muted-foreground">{t("headerMenu.notif2Time", "1 hour ago")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem className="flex-col items-start gap-0.5">
            <span className="text-sm font-medium">{t("headerMenu.notif3Title", "New review posted")}</span>
            <span className="text-xs text-muted-foreground">{t("headerMenu.notif3Time", "Yesterday")}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="justify-center text-sm font-medium text-primary">
            {t("headerMenu.viewAll", "View all")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="grid h-9 w-9 place-items-center rounded-full bg-primary text-xs font-semibold text-primary-foreground shadow-soft transition-transform hover:scale-[1.03]"
            aria-label={t("headerMenu.profile", "Profile")}
          >
            {initials}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>{t("headerMenu.myAccount", "My account")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/dashboard">{t("headerMenu.dashboard", "Dashboard")}</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/admin/settings">{t("headerMenu.settings", "Settings")}</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/help">{t("headerMenu.help", "Help")}</Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/login">{t("headerMenu.logout", "Log out")}</Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
